import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LegalEntityDetails } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, startWith, switchMap } from 'rxjs';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { QualificationTabComponent } from './qualification-tab/qualification-tab.component';
import { RepresentativesTabComponent } from './representatives-tab/representatives-tab.component';

type Tab = 'details' | 'representatives' | 'qualification';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-legal-entities-details',
  standalone: true,
  templateUrl: './legal-entity-details.component.html',
  styleUrl: './legal-entity-details.component.scss',
  imports: [
    CommonModule,
    TranslatePipe,
    DetailsPageHeaderComponent,
    DetailsTabComponent,
    RepresentativesTabComponent,
    QualificationTabComponent,
    ButtonComponent,
  ],
})
export class LegalEntityDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  legalEntity = signal<LegalEntityDetails | null>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  isCancelling = signal(false);
  error = signal<string | null>(null);
  isToggling = signal(false);
  isDeleting = signal(false);

  canDelete = computed(() => !this.isDeleting() && !this.isToggling());
  canToggle = computed(() => !this.isToggling() && !this.isDeleting());

  activeTab = signal<Tab>('details');
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.legalEntities.tabs.details' },
    {
      key: 'representatives',
      label: 'admin.legalEntities.tabs.representatives',
    },
    { key: 'qualification', label: 'admin.legalEntities.tabs.qualification' },
  ];

  private legalEntityIdSig = toSignal(
    this.route.paramMap.pipe(startWith(this.route.snapshot.paramMap)),
    { requireSync: true }
  ).bind(this) as () => URLSearchParams | any;

  entityId = computed(() => {
    const pm = this.legalEntityIdSig?.();
    return pm?.get?.('legalEntityId') ?? pm?.get?.('id') ?? '';
  });

  @ViewChild(DetailsTabComponent) detailsTab?: DetailsTabComponent;

  constructor() {
    effect(() => {
      const id = this.entityId();
      if (!id) {
        this.isLoading.set(false);
        this.error.set('Missing id');
        return;
      }
      this.loadEntity(id);
    });
  }

  private loadEntity(id: string): void {
    this.isLoading.set(true);
    this.legalEntityService
      .getLegalEntityById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (entity) => {
          this.legalEntity.set(entity as unknown as LegalEntityDetails);
          this.error.set(null);
        },
        error: (err) => {
          this.error.set(err?.message ?? 'Failed to load');
          this.legalEntity.set(null);
        },
      });
  }

  goBack(): void {
    const target = history.state?.from || '/admin/legal-entities';
    this.router.navigate([target], { relativeTo: this.route });
  }

  setTab(tab: Tab): void {
    this.activeTab.set(tab);
  }

  onSaveDetails(): void {
    const save$ = this.detailsTab?.save();
    if (!save$) return;

    this.isSaving.set(true);
    save$
      .pipe(
        switchMap(() =>
          this.legalEntityService.getLegalEntityById(this.entityId())
        ),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        next: (fresh) =>
          this.legalEntity.set(fresh as unknown as LegalEntityDetails),
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        },
      });
  }

  onCancelDetails(): void {
    this.detailsTab?.cancel();
  }

  private getSuspendedFlag(entity: LegalEntityDetails | null): boolean {
    if (entity?.suspended !== undefined) return !!entity.suspended;
    return false;
  }

  get isSuspended(): boolean {
    return this.getSuspendedFlag(this.legalEntity());
  }

  get isDeactivated(): boolean {
    return this.isSuspended;
  }

  onToggleActivation(): void {
    const id = this.entityId();
    if (!id || this.isToggling()) return;

    const currentlySuspended = this.isSuspended;
    this.isToggling.set(true);

    const req$ = currentlySuspended
      ? this.legalEntityService.deSuspendLegalEntity(id)
      : this.legalEntityService.suspendLegalEntity(id);

    req$
      .pipe(
        switchMap(() => this.legalEntityService.getLegalEntityById(id)),
        finalize(() => this.isToggling.set(false))
      )
      .subscribe({
        next: (entity) => {
          this.legalEntity.set(entity as unknown as LegalEntityDetails);
          const okKey = currentlySuspended
            ? 'admin.toasts.entityDeSuspended'
            : 'admin.toasts.entitySuspended';
          this.toast.success(this.translate.instant(okKey));
        },
        error: () => {
          const errKey = currentlySuspended
            ? 'admin.toasts.entityDeSuspendFailed'
            : 'admin.toasts.entitySuspendFailed';
          this.toast.error(this.translate.instant(errKey));
        },
      });
  }

  onDelete(): void {
    const id = this.entityId();
    if (!id || this.isDeleting()) return;

    this.isDeleting.set(true);
    this.legalEntityService
      .deleteLegalEntity(id)
      .pipe(finalize(() => this.isDeleting.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.toasts.deletedSuccess')
          );
          this.router.navigate(['/admin/legal-entities']);
        },
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.deleteFailed'));
        },
      });
  }
}
